import { getUserById } from './userUtils';

/**
 * Helper function to filter users by search query
 * @param {Array} users - Array of user objects
 * @param {string} query - Search query string
 * @returns {Array} - Filtered users matching the query
 */
const filterUsersByQuery = (users, query) => {
  if (!query.trim().length) {
    return [...users];
  }

  const queryLower = query.toLowerCase();
  return users.filter((user) => {
    return (
      user.username.toLowerCase().startsWith(queryLower) ||
      (user.email && user.email.toLowerCase().includes(queryLower))
    );
  });
};

/**
 * Filters and searches available managers for AutoComplete dropdown
 * @param {Object} event - AutoComplete event: { query: 'search_text' } - query contains the text user typed in input
 * @param {Array} users - All users array: [{ id: 1, username: 'john', email: 'john@example.com' }, { id: 2, username: 'jane' }, ...]
 * @param {Array} globalSelectedManagers - Global manager tracking: [{ node_id: 'node1', manager: [1,2] }, { node_id: 'node2', manager: 5 }, ...]
 *                                          NOTE: manager is ARRAY for Level 1, SINGLE VALUE for Level 2+
 * @param {Array} globalSelectedEmployees - Global employee tracking: [{ node_id: 'node1', employees: [3,4,5] }, { node_id: 'node2', employees: [6,7] }, ...]
 * @param {Object} node - Current node: { id: 'node3', level: 1, name: 'Engineering', manager: [1,2], head: [3], employees: [4,5,6] }
 * @param {Array} globalSelectedHeads - Global head tracking: [{ node_id: 'node1', heads: [7,8] }, { node_id: 'node2', heads: [9] }, ...]
 * @param {Array|Object} currentHead - Current locally selected head(s): [{ id: 1, username: 'john' }] or { id: 1, username: 'john' } or null
 * @returns {Array} - Filtered array of user objects available as managers
 *
 * Logic: Excludes users who are already:
 * - Managers in other nodes (from globalSelectedManagers)
 * - Employees anywhere (from globalSelectedEmployees)
 * - Heads anywhere (from globalSelectedHeads)
 * - Current node's head (from currentHead)
 *
 * Example: [{ id: 1, username: 'john_doe' }, { id: 2, username: 'jane_smith' }]
 */
export const searchManagers = (
  event,
  users,
  globalSelectedManagers,
  globalSelectedEmployees,
  node,
  globalSelectedHeads,
  currentHead = null
) => {
  const availableManagers = users.filter((user) => {
    const isAlreadyManager = globalSelectedManagers?.some((item) => {
      if (item.node_id !== node.id) {
        // For other nodes, check if user is in their manager(s)
        return Array.isArray(item.manager)
          ? item.manager.includes(user.id)
          : item.manager === user.id;
      }
      return false;
    });
    const isAlreadyEmployee = globalSelectedEmployees?.some((item) =>
      item.employees.includes(user.id)
    );
    const isAlreadyHead = globalSelectedHeads?.some((item) =>
      item.heads.includes(user.id)
    );

    // check if user is the current locally selected head
    let isCurrentHead = false;
    if (currentHead) {
      if (Array.isArray(currentHead)) {
        // level 1: multiple heads possible
        isCurrentHead = currentHead.some(
          (head) => head && head.id && head.id === user.id
        );
      } else {
        // level 2+: single head
        isCurrentHead = currentHead.id && currentHead.id === user.id;
      }
    }

    return (
      !isAlreadyManager &&
      !isAlreadyEmployee &&
      !isAlreadyHead &&
      !isCurrentHead
    );
  });

  return filterUsersByQuery(availableManagers, event.query);
};

/**
 * Filters and searches available employees for AutoComplete dropdown
 * @param {Object} event - AutoComplete event: { query: 'search_text' } - query contains the text user typed in input
 * @param {Array} users - All users array: [{ id: 1, username: 'john', email: 'john@example.com' }, { id: 2, username: 'jane' }, ...]
 * @param {Object} data - Current form data: { name: 'Engineering', description: 'Tech team', manager: [1,2] or 3, head: [4,5] or 6, employees: [7,8,9] }
 * @param {Array} globalSelectedEmployees - Global employee tracking: [{ node_id: 'node1', employees: [3,4,5] }, { node_id: 'node2', employees: [6,7] }, ...]
 * @param {Array} globalSelectedManagers - Global manager tracking: [{ node_id: 'node1', manager: [1,2] }, { node_id: 'node2', manager: 5 }, ...]
 *                                          NOTE: manager is ARRAY for Level 1, SINGLE VALUE for Level 2+
 * @param {Object} node - Current node: { id: 'node3', level: 2, name: 'Frontend', manager: 8, head: 9, employees: [10,11] }
 * @param {Array} globalSelectedHeads - Global head tracking
 * @param {Array|Object} currentHead - Current locally selected head(s): [{ id: 1, username: 'john' }] or { id: 1, username: 'john' } or null
 * @returns {Array} - Filtered array of user objects available as employees
 *
 * Logic: Excludes users who are:
 * - Current node's manager (from data.manager) - prevents manager being employee
 * - Already employees in other nodes (from globalSelectedEmployees)
 * - Already managers in other nodes (from globalSelectedManagers)
 * - Already heads (from globalSelectedHeads)
 * - Current node's head (from currentHead)
 *
 * Example: [{ id: 3, username: 'bob_wilson' }, { id: 4, username: 'alice_brown' }]
 */
export const searchEmployees = (
  event,
  users,
  data,
  globalSelectedEmployees,
  globalSelectedManagers,
  node,
  globalSelectedHeads = [],
  currentHead = null
) => {
  const availableEmployees = users.filter((user) => {
    const isCurrentManager = Array.isArray(data.manager)
      ? data.manager.includes(user.id)
      : user.id === data.manager;
    const isAlreadyEmployee = globalSelectedEmployees?.some(
      (item) => item.employees.includes(user.id) && item.node_id !== node.id
    );
    const isAlreadyManager = globalSelectedManagers?.some((item) => {
      if (item.node_id !== node.id) {
        return Array.isArray(item.manager)
          ? item.manager.includes(user.id)
          : item.manager === user.id;
      }
      return false;
    });
    let isAlreadyHead = false;

    if (globalSelectedHeads && globalSelectedHeads.length > 0) {
      isAlreadyHead = globalSelectedHeads?.some((item) => {
        return item.heads.includes(user.id);
      });
    }

    // check if user is the current locally selected head
    let isCurrentHead = false;
    if (currentHead) {
      if (Array.isArray(currentHead)) {
        // level 1: multiple heads possible
        isCurrentHead = currentHead.some(
          (head) => head && head.id && head.id === user.id
        );
      } else {
        // level 2+: single head
        isCurrentHead = currentHead.id && currentHead.id === user.id;
      }
    }

    return (
      !isCurrentManager &&
      !isAlreadyEmployee &&
      !isAlreadyManager &&
      !isAlreadyHead &&
      !isCurrentHead
    );
  });

  return filterUsersByQuery(availableEmployees, event.query);
};

/**
 * Filters and searches available heads for AutoComplete dropdown (Level 1 nodes only)
 * @param {Object} event - AutoComplete event: { query: 'search_text' } - query contains the text user typed in input
 * @param {Array} users - All users array: [{ id: 1, username: 'john', email: 'john@example.com' }, { id: 2, username: 'jane' }, ...]
 * @param {Array} globalSelectedHeads - Global head tracking: [{ node_id: 'node1', heads: [7,8] }, { node_id: 'node2', heads: [9] }, ...]
 * @param {Array} globalSelectedManagers - Global manager tracking: [{ node_id: 'node1', manager: [1,2] }, { node_id: 'node2', manager: 5 }, ...]
 *                                          NOTE: manager is ARRAY for Level 1, SINGLE VALUE for Level 2+
 * @param {Array} globalSelectedEmployees - Global employee tracking: [{ node_id: 'node1', employees: [3,4,5] }, { node_id: 'node2', employees: [6,7] }, ...]
 * @param {Object} node - Current node: { id: 'node3', level: 1, name: 'Engineering', manager: [1,2], head: [3,4], employees: [5,6,7] }
 * @param {Array|Object} currentManagers - Current selected managers: [{ id: 1, username: 'john' }, { id: 2, username: 'jane' }] for Level 1, { id: 3, username: 'bob' } for Level 2+
 * @returns {Array} - Filtered array of user objects available as heads
 *
 * Logic: Excludes users who are:
 * - Already heads in other nodes (from globalSelectedHeads)
 * - Already managers anywhere (from globalSelectedManagers)
 * - Already employees anywhere (from globalSelectedEmployees)
 * - Current node's managers (from currentManagers) - prevents manager being head as level 1 managers are optinal
 *
 * Example: [{ id: 5, username: 'charlie_davis' }, { id: 6, username: 'diana_lee' }]
 */
export const searchHeads = (
  event,
  users,
  globalSelectedHeads,
  globalSelectedManagers,
  globalSelectedEmployees,
  node,
  currentManagers
) => {
  const availableHeads = users.filter((user) => {
    const isAlreadyHead = globalSelectedHeads?.some(
      (item) => item.heads.includes(user.id) && item.node_id !== node.id
    );
    const isAlreadyManager = globalSelectedManagers?.some((item) => {
      return Array.isArray(item.manager)
        ? item.manager.includes(user.id)
        : item.manager === user.id;
    });
    const isAlreadyEmployee = globalSelectedEmployees?.some((item) =>
      item.employees.includes(user.id)
    );
    const isCurrentManager = currentManagers?.some(
      (manager) => manager.id === user.id
    );
    return (
      !isAlreadyHead &&
      !isAlreadyManager &&
      !isAlreadyEmployee &&
      !isCurrentManager
    );
  });

  return filterUsersByQuery(availableHeads, event.query);
};

/**
 * Filters and searches available single heads for AutoComplete dropdown (Level 2 nodes only)
 * @param {Object} event - AutoComplete event: { query: 'search_text' } - query contains the text user typed in input
 * @param {Object} node - Current node: { id: 'node2', level: 2, name: 'Frontend', manager: 8, head: [3,4], employees: [10,11] }
 * @param {Array} users - All users array: [{ id: 1, username: 'john', email: 'john@example.com' }, { id: 2, username: 'jane' }, ...]
 * @returns {Array} - Filtered array of user objects available as single head
 *
 * Logic: For Level 2 nodes, only shows heads from parent node's head array
 * - Gets parent's heads from node.head array (contains parent head IDs: [3,4])
 * - Maps IDs to user objects: [{ id: 3, username: 'charlie' }, { id: 4, username: 'diana' }]
 * - Filters by search query (text user typed in AutoComplete input)
 *
 * Example: [{ id: 7, username: 'parent_head_user' }]
 */
export const searchSingleHeads = (event, node, users) => {
  // For level 2, get available heads from the parent's head array
  const availableHeads =
    node?.head && Array.isArray(node.head)
      ? node.head.map((id) => getUserById(id, users)).filter(Boolean)
      : [];

  return filterUsersByQuery(availableHeads, event.query);
};
